import React, { useState, useEffect } from 'react';
import { GetAll, apiURL } from '../../utils/apiCalls';

// Cache for ads data to avoid redundant API calls
const adsCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Reusable Ad Display Component
 * Fetches and displays ads based on position_id
 * Optimized with caching to reduce API calls and improve performance
 */
const AdDisplay = ({ positionId, className = '', style = {}, onAdClick }) => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);

  useEffect(() => {
    const fetchAds = async () => {
      if (!positionId) {
        setLoading(false);
        return;
      }

      // Check cache first
      const cacheKey = `position_${positionId}`;
      const cached = adsCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        setAds(cached.data);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Fetch all ads in a single call (cached at API level if possible)
        const response = await GetAll(apiURL + '/api/v1/ads');
        
        if (response && response.data && response.data.status) {
          // Filter ads by position_id
          const allAds = response.data.data || [];
          const filteredAds = allAds.filter(ad => String(ad.position_id) === String(positionId));
          
          if (filteredAds.length === 0) {
            setLoading(false);
            // Cache empty result to avoid repeated calls
            adsCache.set(cacheKey, { data: [], timestamp: Date.now() });
            return;
          }
          
          // Batch fetch uploads for all ads in parallel (optimized)
          // Use Promise.allSettled to handle partial failures gracefully
          const uploadPromises = filteredAds.map(async (ad) => {
            try {
              // Use the correct endpoint: /api/v1/ads/:id/uploads
              const assetsResponse = await GetAll(apiURL + `/api/v1/ads/${ad.id}/uploads`);
              const uploads = assetsResponse?.data?.data || [];
              // Convert Bookshelf collection to array if needed
              const assets = Array.isArray(uploads) ? uploads : (uploads.toJSON ? uploads.toJSON() : []);
              return { ...ad, assets };
            } catch (err) {
              console.error(`Error fetching uploads for ad ${ad.id}:`, err);
              return { ...ad, assets: [] };
            }
          });
          
          const results = await Promise.allSettled(uploadPromises);
          const adsWithAssets = results
            .filter(result => result.status === 'fulfilled')
            .map(result => result.value);
          
          // Filter ads to only include those with valid uploads
          // Check if status field exists and filter accordingly
          const validAds = adsWithAssets.filter(ad => {
            if (!ad.assets || ad.assets.length === 0) return false;
            
            // Check if status field exists in any asset
            const hasStatusField = ad.assets.some(asset => asset.hasOwnProperty('status'));
            
            if (hasStatusField) {
              // Filter by status: show 'completed' or if no completed, show 'uploading'
              const completedAssets = ad.assets.filter(asset => asset.status === 'completed');
              const uploadingAssets = ad.assets.filter(asset => asset.status === 'uploading');
              // Prefer completed, but also show uploading if no completed exists
              const validAssets = completedAssets.length > 0 ? completedAssets : uploadingAssets;
              // Also check if asset has filepaths3 URL
              const assetsWithUrl = validAssets.filter(asset => asset.filepaths3);
              return assetsWithUrl.length > 0;
            } else {
              // If status field doesn't exist, show all assets with URLs (backward compatibility)
              const assetsWithUrl = ad.assets.filter(asset => asset.filepaths3);
              return assetsWithUrl.length > 0;
            }
          });
          
          // Cache the results
          adsCache.set(cacheKey, { data: validAds, timestamp: Date.now() });
          setAds(validAds);
        }
      } catch (error) {
        console.error('Error fetching ads:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAds();
  }, [positionId]);

  // Rotate ads every 10 seconds if multiple ads
  useEffect(() => {
    if (ads.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentAdIndex((prev) => (prev + 1) % ads.length);
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, [ads.length]);

  if (loading) {
    return null; // Don't show anything while loading
  }

  if (ads.length === 0) {
    return null; // Don't show anything if no ads
  }

  const currentAd = ads[currentAdIndex];
  
  // Get the first valid asset (prefer completed, fallback to uploading)
  let assets = currentAd?.assets || [];
  
  // If status field exists, prefer completed, but also allow uploading
  if (assets.length > 0 && assets[0].hasOwnProperty('status')) {
    const completedAssets = assets.filter(asset => asset.status === 'completed');
    const uploadingAssets = assets.filter(asset => asset.status === 'uploading');
    // Prefer completed, but use uploading if no completed exists
    assets = completedAssets.length > 0 ? completedAssets : uploadingAssets;
  }
  
  // Filter to only assets with URLs
  assets = assets.filter(asset => asset.filepaths3);
  
  const currentAsset = assets[0];

  if (!currentAsset) {
    return null;
  }

  // Use file_ext and file_name to determine file type (from database schema)
  const fileExt = currentAsset.file_ext?.toLowerCase() || '';
  const fileName = currentAsset.file_name?.toLowerCase() || '';
  
  const isVideo = /\.(mp4|mov|avi|wmv|flv|webm|mkv)$/i.test(fileExt) || 
                  /\.(mp4|mov|avi|wmv|flv|webm|mkv)$/i.test(fileName);
  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(fileExt) || 
                  /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
  
  // Use filepaths3 from database schema
  const assetUrl = currentAsset.filepaths3;
  
  if (!assetUrl) {
    return null;
  }

  const handleClick = () => {
    if (onAdClick && currentAd.link_url) {
      onAdClick(currentAd.link_url);
    } else if (currentAd.link_url) {
      window.open(currentAd.link_url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className={className} style={style}>
      {isVideo ? (
        <video
          src={assetUrl}
          autoPlay
          loop
          muted
          playsInline
          style={{ width: '100%', height: '100%', objectFit: 'contain', cursor: currentAd.link_url ? 'pointer' : 'default' }}
          onClick={handleClick}
        />
      ) : isImage ? (
        <img
          src={assetUrl}
          alt={currentAd.name || 'Advertisement'}
          style={{ width: '100%', height: '100%', objectFit: 'contain', cursor: currentAd.link_url ? 'pointer' : 'default' }}
          onClick={handleClick}
        />
      ) : null}
    </div>
  );
};

export default AdDisplay;

